import React from 'react';

import { formatMoney } from '@betterment/core';
import { useGraphQLQuery } from '@betterment/graphql';
import { Box, Icon, Separator, Text } from '@betterment/samba-react';
import { CardLoader, SambaAutoLink } from '@betterment/shared-components';
import { ThemedImage } from '@betterment/shared-components/src/components/themed-image/ThemedImage';
import { isNil } from 'es-toolkit/compat';

import { DateRangeOption } from '../../../../../graphql/schema';
import { titleCss } from '../../../../../Shared/components-v2/styled-link-row/StyledLinkRow';
import { urlBuilder } from '../../../../../Shared/urlBuilder';
import { PerformanceAccountTimeWeightedDocument } from '../graphql/performanceAccountTimeWeighted.generated';

// Shared layout constants
const ICON_SIZE = 'size.450';
const CARET_WIDTH = 'size.300';
const BALANCE_WIDTH = '33%';

const iconCss = { width: ICON_SIZE, height: ICON_SIZE, flexShrink: 0 };
const accountColumnCss = { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' };
const balanceColumnCss = {
  width: BALANCE_WIDTH,
  textAlign: 'right',
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  marginRight: '-space.100',
};
const caretCss = { width: CARET_WIDTH, flexShrink: 0 };

export const CashAccountRowHeader: React.FC = () => (
  <>
    <Box
      css={{
        display: 'flex',
        alignItems: 'center',
        gap: 'space.150',
        paddingX: 'space.300',
        paddingY: 'space.150',
      }}
      aria-label="Column headers"
    >
      <Box css={accountColumnCss}>
        <Text variant="text50">Account</Text>
      </Box>
      <Box css={{ width: ICON_SIZE, flexShrink: 0 }} />
      <Box css={balanceColumnCss}>
        <Text variant="text50">Balance</Text>
      </Box>
      <Box css={caretCss} aria-hidden="true" />
    </Box>
    <Separator css={{ fullWidthWithInset: 'space.300' }} />
  </>
);

export const CashAccountRow: React.FC<{
  accountId: string;
  dateRangeOption: DateRangeOption;
}> = ({ accountId, dateRangeOption }) => {
  const { data } = useGraphQLQuery(PerformanceAccountTimeWeightedDocument, {
    variables: { accountIds: [accountId], dateRangeOption },
    throwOnError: true,
  });
  if (!data) return <CardLoader />;

  const account = data.performance.accounts?.[0];
  if (isNil(account)) return null;

  const { id, icon: accountIcon, name: accountName, envelope } = account;
  const apy = account.__typename === 'CashAccount' ? account.apy?.rate : undefined;
  const { purpose } = envelope;
  const name = purpose?.name || accountName;
  const icon = purpose?.image || accountIcon;
  const { earned, endingBalance: balance } = data.performance.totals;

  return (
    <>
      <Box className="group">
        <SambaAutoLink
          to={urlBuilder.appAccountPerformance.absolute({
            accountId: id,
            returnTo: urlBuilder.appPerformance.absolute(),
            returnLabel: 'All performance',
          })}
          css={{
            display: 'flex',
            alignItems: 'center',
            gap: 'space.150',
            padding: 'space.300',
            width: '100%',
            textDecoration: 'none',
            '&:hover': { textDecoration: 'none' },
            '&:focus': { textDecoration: 'none' },
          }}
          aria-label={name}
        >
          <Box css={iconCss}>
            <ThemedImage themedAsset={icon} alt="" aria-hidden="true" />
          </Box>

          <Box css={accountColumnCss}>
            <Text color="text" css={titleCss}>
              {name}
            </Text>
            {apy && (
              <Text color="text.weak" variant="text50">
                {apy}% APY*
              </Text>
            )}
          </Box>

          <Box css={balanceColumnCss}>
            <Text css={{ textAlign: 'right', color: 'text' }}>{formatMoney(balance)}</Text>
            <Text variant="text50" css={{ textAlign: 'right', color: earned.cents > 0 ? 'text.success' : 'text' }}>
              {formatMoney(earned)}
            </Text>
          </Box>

          <Box css={caretCss}>
            <Icon color="icon" name="CaretRight" size="small" aria-hidden="true" />
          </Box>
        </SambaAutoLink>
      </Box>
      <Separator css={{ fullWidthWithInset: 'space.300' }} />
    </>
  );
};
